import { useEffect, useMemo } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  CONNECTED_SIGNALS_WEIGHT_FILTER,
  connectedSignalsResultResponse,
  createCasesApi,
  createConnectedSignalsApi,
  createSignalsApi,
  createStreamsApi,
  createWorkbooksApi,
  durationToMs,
  isTerminal,
  readConnectedSignalsResult,
} from '@bloo-team/api-client'
import type { CsSubgraph, SearchResultPage, SignalAssignStatus, SignalRow, SignalTag, WorkbookLockCheck } from '@bloo-team/api-client'
import { client, searchApiFor } from '@bloo-team/core-shell'
import { isDemoSession } from '@bloo-team/core-shell'
import { DEMO_NOW, demoSignalRows } from './signals-demo'
import { matchesFilter, readSignal, readSignals, type Signal } from './signal-model'
import { fpFilter, isSafeStream, toAddFilters, wireTime, type FlatQuery } from './signal-addfilters'
import { matchesStage, STAGE_NONE } from './signal-queue'
import { connectedSignals, workerRow } from './connected-signals'
import { readSubgraphs, type Subgraph } from './signal-subgraphs'
import { attributionPayload, demoAttribution, type Campaign } from './signal-attribution'
import { demoSignalKb, fetchEvidence, type SignalEvidence } from './signal-evidence'
import { attachSignalsTo, createCmCase } from './case-mgmt'
import type { CmSeverity } from '@bloo-team/api-client'
import { sleep, throwIfAborted } from './poll'
import type { TimeBucket } from '@bloo-team/core-shell'

/**
 * Signals — data hooks over the legacy signal + case endpoints, with the
 * demo/live switch every other Vantage screen uses (getSession().mode).
 *
 * Demo mode serves the authored fixture (signals-demo.ts) filtered by
 * window, and an in-memory case store, so the whole triage loop runs with
 * no backend. Live mode goes through api-client; ⚠️ shapes there are
 * unverified until /api/signals/* is captured (docs/signals-requirements.md
 * §7 item 4).
 */

export const liveSignalsApi = createSignalsApi(client)
export const liveCasesApi = createCasesApi(client)
const liveStreamsApi = createStreamsApi(client)
const liveWorkbooksApi = createWorkbooksApi(client)

export const WINDOW_OPTIONS = ['1d', '7d', '30d'] as const
export type SignalWindow = (typeof WINDOW_OPTIONS)[number]

/** Live mode pages at this size; the queue reads up to LIVE_MAX rows. */
const LIVE_PAGE = 500
export const LIVE_MAX = 2000

/* ---------------------------------------------------------------- demo */

let demoRowsCache: { base: number; rows: SignalRow[] } | null = null

/**
 * Anchored to page load (DEMO_NOW, shared with the EP-PROCESS search fixture
 * so a signal's raw events fall inside its ±15 min window). Not regenerated
 * during the tab's life: ages drift, timestamps stay stable across refetches.
 */
function demoRows(): SignalRow[] {
  if (!demoRowsCache) demoRowsCache = { base: DEMO_NOW, rows: demoSignalRows(DEMO_NOW) }
  return demoRowsCache.rows
}

/** Test hook: reset the fixture cache. */
export function resetDemoSignals() {
  demoRowsCache = null
}

/**
 * The anchored demo fixture rows, shared with every demo consumer (the
 * overview's posture derivation reads the same rows the queue lists, so the
 * two screens agree on every count).
 */
export function demoSignalRowsCached(): SignalRow[] {
  return demoRows()
}

/**
 * Every signal uuid some legacy demo case holds — half of the demo's
 * `assign_status` split. The split is the legacy signals service's: V2
 * attachments are not in it (docs/cases.md §Signals).
 */
async function demoCasedUuids(): Promise<Set<string>> {
  return new Set((await demoCasesApi.list()).flatMap((c) => c.signals))
}

/** The server's `unassigned`: in no case, not flagged false positive, no remark. */
const demoUnassigned = (r: SignalRow, cased: ReadonlySet<string>) =>
  !cased.has(String(r.uuid)) && r.falsepositive !== true && !String(r.signal_remark ?? '').trim()

/** Exported for tests (a page test makes a demo write fail at this seam). */
export const demoSignalsApi = {
  /** Emulates the server's queue split (signals/list `assign_status`). */
  list: async (window: SignalWindow, assignStatus?: SignalAssignStatus): Promise<SignalRow[]> => {
    const since = Date.now() - (durationToMs(window) ?? 7 * 86_400_000)
    const rows = demoRows().filter((r) => Number(r.cnamtime) >= since)
    if (!assignStatus) return rows
    const cased = await demoCasedUuids()
    return rows.filter((r) => demoUnassigned(r, cased) === (assignStatus === 'unassigned'))
  },
  /** One Flat page: the window + tab, then the same filters the live addfilters carry, newest first. */
  page: async (window: SignalWindow, q: FlatPageQuery): Promise<{ rows: SignalRow[]; count: number }> => {
    const rows = await demoSignalsApi.list(window, q.assignStatus)
    const f = q.flat
    const keep = rows.filter((r) => {
      const s = readSignal(r)
      if (!s) return false
      if (f.wbId && s.detectionId !== f.wbId) return false
      if (f.range && (s.at < f.range.start || s.at >= f.range.end)) return false
      // Streams: what the live addfilters can send (CB-67 guard), so demo agrees.
      return matchesFilter(s, { ...f.filter, streams: f.filter.streams.filter(isSafeStream), openOnly: false }) && matchesStage(flatStages(f.stages), s)
    })
    keep.sort((a, b) => Number(b.cnamtime) - Number(a.cnamtime))
    const from = (q.pageNo - 1) * q.pageSize
    return { rows: keep.slice(from, from + q.pageSize), count: keep.length }
  },
  data: async (uuid: string): Promise<SignalRow | null> => demoRows().find((r) => r.uuid === uuid) ?? null,
  addRemark: async (uuid: string, remark: string) => demoPatchRows((r) => (r.uuid === uuid ? { ...r, signal_remark: remark } : r)),
  flagFalsePositive: async (uuids: string[], fp: boolean) =>
    demoPatchRows((r) => (uuids.includes(String(r.uuid)) ? { ...r, falsepositive: fp } : r)),
}

/**
 * A demo write REPLACES the touched rows: patched in place, a refetch would
 * hand React Query an equal-looking tree and structural sharing would keep
 * the stale parse.
 */
function demoPatchRows(patch: (r: SignalRow) => SignalRow) {
  demoRows()
  demoRowsCache!.rows = demoRowsCache!.rows.map(patch)
}

export interface DemoCaseNote {
  author: string
  text: string
  at: number
}

export interface DemoCaseEvent {
  action: string
  author: string
  at: number
  /** Opaque detail string, python-repr-ish like the live case_history. */
  event: string
}

/**
 * The demo case record — the in-memory mirror of the VERIFIED live case doc
 * (docs/inventory/cases-write-capture.json): status uses the change_status
 * enum, tta/ttr are seconds, notes carry the backend's system-note behavior
 * (author 'Bloo'), history mirrors case_history rows.
 */
export interface DemoCase {
  id: string
  title: string
  severity: string
  createdAt: number
  signals: string[]
  status: 'OPEN' | 'IN-PROGRESS' | 'ON-HOLD' | 'CLOSED'
  handler: string | null
  handlerId: string | null
  followerList: string[]
  /** Seconds, like the live tta/ttr floats. */
  tta: number | null
  ttr: number | null
  notes: DemoCaseNote[]
  history: DemoCaseEvent[]
  /** Storyline (subgraph) names mapped to this case — the demo's case_graph rows. */
  graphs?: string[]
}

const MIN = 60_000
const DAY = 86_400_000

/** System note + matching history entry, the way the live backend writes them. */
function opened(who: string, title: string, severity: string, at: number): Pick<DemoCase, 'notes' | 'history'> {
  return {
    notes: [{ author: 'Bloo', text: `${who} opened this case`, at }],
    history: [{ action: 'ADD', author: who, at, event: `{'case_name': '${title}', 'severity': '${severity}'}` }],
  }
}

/**
 * Three authored cases over the signal fixture's storyline uuids
 * (signals-demo.ts emit order: sig-0006..0008 = r.okafor chain,
 * sig-0015..0017 = m.chen chain, sig-0018..0020 = build-agent-13 chain).
 * Anchored to DEMO_NOW so ages stay stable across refetches.
 */
function seedDemoCases(): DemoCase[] {
  const okafor = opened('Reena Patil', 'Suspicious admin share activity — hr-fs-01', 'high', DEMO_NOW - 155 * MIN)
  const chen = opened('Demo Analyst', 'Brute force against dc-01', 'medium', DEMO_NOW - 4 * DAY)
  const ci = opened('Demo Analyst', 'CI runner persistence — build-agent-13', 'low', DEMO_NOW - 6 * DAY)
  return [
    {
      id: 'CASE-1041',
      title: 'Suspicious admin share activity — hr-fs-01',
      severity: 'high',
      createdAt: DEMO_NOW - 155 * MIN,
      signals: ['sig-0006', 'sig-0007', 'sig-0008'],
      status: 'IN-PROGRESS',
      handler: 'Reena Patil',
      handlerId: '8543e0b5-9f4e-416e-9378-3c393193219c',
      followerList: ['analyst@bloo.io'],
      tta: 540,
      ttr: null,
      notes: [
        ...okafor.notes,
        { author: 'Bloo', text: 'Reena Patil added 3 new signal(s)', at: DEMO_NOW - 154 * MIN },
        { author: 'reena@bloo.io', text: 'VPN geo does not match travel records — pulling admin-share sessions on hr-fs-01.', at: DEMO_NOW - 140 * MIN },
      ],
      history: [
        ...okafor.history,
        { action: 'ADD SIGNAL sig-0006 TO CASE', author: 'Reena Patil', at: DEMO_NOW - 154 * MIN, event: "{'signal_list': ['sig-0006']}" },
        { action: 'ADD SIGNAL sig-0007 TO CASE', author: 'Reena Patil', at: DEMO_NOW - 154 * MIN, event: "{'signal_list': ['sig-0007']}" },
        { action: 'ADD SIGNAL sig-0008 TO CASE', author: 'Reena Patil', at: DEMO_NOW - 154 * MIN, event: "{'signal_list': ['sig-0008']}" },
        { action: 'CHANGE STATUS', author: 'Reena Patil', at: DEMO_NOW - 146 * MIN, event: "{'status': 'IN-PROGRESS', 'reason': ''}" },
      ],
    },
    {
      id: 'CASE-1037',
      title: 'Brute force against dc-01',
      severity: 'medium',
      createdAt: DEMO_NOW - 4 * DAY,
      signals: ['sig-0015', 'sig-0016', 'sig-0017'],
      status: 'OPEN',
      handler: null,
      handlerId: null,
      followerList: [],
      tta: null,
      ttr: null,
      notes: [...chen.notes, { author: 'Bloo', text: 'Demo Analyst added 3 new signal(s)', at: DEMO_NOW - 4 * DAY }],
      history: [
        ...chen.history,
        { action: 'ADD SIGNAL sig-0015 TO CASE', author: 'Demo Analyst', at: DEMO_NOW - 4 * DAY, event: "{'signal_list': ['sig-0015']}" },
        { action: 'ADD SIGNAL sig-0016 TO CASE', author: 'Demo Analyst', at: DEMO_NOW - 4 * DAY, event: "{'signal_list': ['sig-0016']}" },
        { action: 'ADD SIGNAL sig-0017 TO CASE', author: 'Demo Analyst', at: DEMO_NOW - 4 * DAY, event: "{'signal_list': ['sig-0017']}" },
      ],
    },
    {
      id: 'CASE-1029',
      title: 'CI runner persistence — build-agent-13',
      severity: 'low',
      createdAt: DEMO_NOW - 6 * DAY,
      signals: ['sig-0018', 'sig-0019', 'sig-0020'],
      status: 'CLOSED',
      handler: 'Demo Analyst',
      handlerId: 'demo-user',
      followerList: [],
      tta: 1200,
      ttr: 2 * 86_400,
      notes: [
        ...ci.notes,
        { author: 'analyst@bloo.io', text: 'Scheduled task + service install both belong to the CI provisioning job. Benign.', at: DEMO_NOW - 4 * DAY },
        { author: 'Bloo', text: 'Demo Analyst changed status to CLOSED', at: DEMO_NOW - 4 * DAY },
      ],
      history: [
        ...ci.history,
        { action: 'ADD SIGNAL sig-0018 TO CASE', author: 'Demo Analyst', at: DEMO_NOW - 6 * DAY, event: "{'signal_list': ['sig-0018']}" },
        { action: 'CHANGE STATUS', author: 'Demo Analyst', at: DEMO_NOW - 4 * DAY, event: "{'status': 'CLOSED', 'reason': 'CI provisioning job confirmed benign.'}" },
      ],
    },
  ]
}

let demoCases: DemoCase[] = seedDemoCases()
/** Test hook. */
export function resetDemoCases() {
  demoCases = seedDemoCases()
}

function copyCase(c: DemoCase): DemoCase {
  return { ...c, signals: [...c.signals], followerList: [...c.followerList], notes: [...c.notes], history: [...c.history], graphs: [...(c.graphs ?? [])] }
}

/** The demo signal document for one uuid — same anchored fixture rows. */
export function demoSignalRow(uuid: string): SignalRow | null {
  return demoRows().find((r) => r.uuid === uuid) ?? null
}

/**
 * The LEGACY case records the demo signals service still answers from —
 * signals/case_list (row badges, the pane's cases), cases/get_graph (a
 * storyline's mapped case), the Unassigned split and the posture counts.
 * Read only: the console writes cases through case management V2
 * (lib/case-mgmt.ts); legacy ids open there via the legacy-id lookup.
 */
export const demoCasesApi = {
  list: async (): Promise<DemoCase[]> => demoCases.map(copyCase),
}

/* ---------------------------------------------------------------- hooks */

export interface SignalsScope {
  clusterId: string
  scopeId: string
}

/** `fpOnly` lengthens the key, so the 30d baseline's key (refreshAfterSignalWrite) stays as it was. */
const listKey = (q: SignalsScope, window: SignalWindow, demo: boolean, assignStatus?: SignalAssignStatus, fpOnly = false) =>
  ['signals', q.clusterId, q.scopeId, window, demo ? 'demo' : 'live', assignStatus ?? 'all', ...(fpOnly ? ['fp'] : [])] as const

async function liveList(q: SignalsScope, window: SignalWindow, assignStatus?: SignalAssignStatus, fpOnly = false): Promise<SignalRow[]> {
  const rows: SignalRow[] = []
  const addFilters = fpOnly ? [fpFilter(true)] : undefined
  for (let pageNo = 1; rows.length < LIVE_MAX; pageNo++) {
    const page = await liveSignalsApi.list({ ...q, duration: window, pageNo, pageSize: LIVE_PAGE, assignStatus, addFilters })
    rows.push(...page.rows)
    if (page.rows.length < LIVE_PAGE) break
    if (page.count !== null && rows.length >= page.count) break
  }
  return rows.slice(0, LIVE_MAX)
}

/** Every signal in the window, parsed. Refetches on window (and queue split) change only.
 * `enabled: false` makes no request (live: up to LIVE_MAX / LIVE_PAGE calls).
 * `assignStatus` is the server's queue split (signals/list `assign_status`).
 * `fpOnly`: only signals flagged false positive, asked of the server
 * (`falsepositive = true`) — so the False positives queue is not cut to the
 * window's newest LIVE_MAX rows before it is filtered. */
export function useSignals(q: SignalsScope, window: SignalWindow, opts?: { enabled?: boolean; assignStatus?: SignalAssignStatus; fpOnly?: boolean }) {
  const demo = isDemoSession()
  const assignStatus = opts?.assignStatus
  const fpOnly = opts?.fpOnly ?? false
  const query = useQuery<SignalRow[]>({
    queryKey: listKey(q, window, demo, assignStatus, fpOnly),
    queryFn: async () =>
      demo ? (await demoSignalsApi.list(window, assignStatus)).filter((r) => !fpOnly || r.falsepositive === true) : liveList(q, window, assignStatus, fpOnly),
    enabled: opts?.enabled ?? true,
    staleTime: 60_000,
    retry: 1,
  })
  const signals = useMemo(() => readSignals(query.data ?? []), [query.data])
  return { ...query, signals, truncated: (query.data?.length ?? 0) >= LIVE_MAX }
}

/**
 * Every signal between two instants (epoch ms) — the case page's Attach
 * signals search over the Cases time range (lib/cases-range.ts). Live sends
 * the absolute start / end the list takes (UTC); demo cuts its rows by time.
 */
export function useSignalsBetween(q: SignalsScope, bounds: { from: number; to: number }, key: string, opts?: { enabled?: boolean }) {
  const demo = isDemoSession()
  const query = useQuery<SignalRow[]>({
    queryKey: ['signals', q.clusterId, q.scopeId, 'between', key, demo ? 'demo' : 'live'],
    queryFn: async () => {
      if (demo) return demoRows().filter((r) => Number(r.cnamtime) >= bounds.from && Number(r.cnamtime) <= bounds.to)
      const rows: SignalRow[] = []
      for (let pageNo = 1; rows.length < LIVE_MAX; pageNo++) {
        const page = await liveSignalsApi.list({ ...q, startTime: wireTime(bounds.from), endTime: wireTime(bounds.to), timezone: 'UTC', pageNo, pageSize: LIVE_PAGE })
        rows.push(...page.rows)
        if (page.rows.length < LIVE_PAGE) break
        if (page.count !== null && rows.length >= page.count) break
      }
      return rows.slice(0, LIVE_MAX)
    },
    enabled: opts?.enabled ?? true,
    staleTime: 60_000,
    retry: 1,
  })
  const signals = useMemo(() => readSignals(query.data ?? []), [query.data])
  return { ...query, signals, truncated: (query.data?.length ?? 0) >= LIVE_MAX }
}

/**
 * The tab's signal total for the window — the Flat list's own call
 * (signals/list) at pageno 1 / pagesize 1 with only the tab's
 * `assign_status`, read for its `count`. The queue tab badge on the lenses
 * that do not load the window rows. `fpOnly`: only signals flagged false
 * positive (the False positives queue, `falsepositive = true`).
 */
export function useSignalCount(q: SignalsScope, window: SignalWindow, assignStatus: SignalAssignStatus | undefined, opts?: { enabled?: boolean; fpOnly?: boolean }) {
  const demo = isDemoSession()
  const fpOnly = opts?.fpOnly ?? false
  return useQuery<number>({
    queryKey: ['signals', q.clusterId, q.scopeId, window, demo ? 'demo' : 'live', 'count', assignStatus ?? 'all', fpOnly ? 'fp' : 'any'],
    queryFn: async () => {
      if (demo) return (await demoSignalsApi.list(window, assignStatus)).filter((r) => !fpOnly || r.falsepositive === true).length
      const res = await liveSignalsApi.list({ ...q, duration: window, pageNo: 1, pageSize: 1, assignStatus, addFilters: fpOnly ? [fpFilter(true)] : undefined })
      return res.count ?? res.rows.length
    },
    enabled: opts?.enabled ?? true,
    staleTime: 60_000,
    retry: 1,
  })
}

export interface FlatPageQuery {
  assignStatus?: SignalAssignStatus
  flat: FlatQuery
  pageNo: number
  pageSize: number
}

/** Demo Flat page — the fixture through the same window / tab / filter rules (exported for tests). */
export function demoSignalPage(window: SignalWindow, q: FlatPageQuery) {
  return demoSignalsApi.page(window, q)
}

/** Demo FP flag + remark writes (exported for tests). */
export const demoSignalWrites = {
  flagFalsePositive: (uuids: string[], fp: boolean) => demoSignalsApi.flagFalsePositive(uuids, fp),
  addRemark: (uuid: string, remark: string) => demoSignalsApi.addRemark(uuid, remark),
}

/** Mixed 'none' + named stages: the server can only send the named ones (signal-addfilters), so demo agrees. */
function flatStages(stages: readonly string[]): readonly string[] {
  const named = stages.filter((s) => s !== STAGE_NONE)
  return named.length ? named : stages
}

/**
 * One server page of the Flat list (signals/list pageno/pagesize + addfilters).
 * The server orders by time (cnamtime desc) and `count` is every match, so the
 * pager reads it. Keeps the previous page on screen while the next loads.
 */
export function useSignalPage(q: SignalsScope, window: SignalWindow, page: FlatPageQuery, opts?: { enabled?: boolean }) {
  const demo = isDemoSession()
  const query = useQuery<{ rows: SignalRow[]; count: number }>({
    queryKey: ['signals', q.clusterId, q.scopeId, window, demo ? 'demo' : 'live', 'page', page],
    queryFn: async () => {
      if (demo) return demoSignalsApi.page(window, page)
      const range = page.flat.range
      const res = await liveSignalsApi.list({
        ...q,
        ...(range ? { startTime: wireTime(range.start), endTime: wireTime(range.end), timezone: 'UTC' } : { duration: window }),
        pageNo: page.pageNo,
        pageSize: page.pageSize,
        assignStatus: page.assignStatus,
        addFilters: toAddFilters(page.flat),
      })
      return { rows: res.rows, count: res.count ?? res.rows.length }
    },
    enabled: opts?.enabled ?? true,
    placeholderData: (prev) => prev,
    staleTime: 60_000,
    retry: 1,
  })
  const signals = useMemo(() => readSignals(query.data?.rows ?? []), [query.data])
  return { ...query, signals, count: query.data?.count ?? 0 }
}

const liveConnectedApi = createConnectedSignalsApi(client)

/** Poll cadence + ceiling — coverage-health.ts fetchLastFired, verbatim (decision S37: no revoke). */
export const CS_POLL_MS = 1500
export const CS_POLL_MAX = 40

/**
 * Invoke → state → result. `signal` (React Query's) stops the loop when the
 * lens unmounts or the window changes: no further state polls, no result
 * read. The job itself is never revoked (S37).
 */
export async function fetchConnectedSignals(q: SignalsScope, window: SignalWindow, signal?: AbortSignal): Promise<CsSubgraph[]> {
  throwIfAborted(signal)
  const taskId = await liveConnectedApi.invoke({ ...q, duration: window, weightFilter: CONNECTED_SIGNALS_WEIGHT_FILTER })
  for (let i = 0; i < CS_POLL_MAX; i++) {
    throwIfAborted(signal)
    const s = await searchApiFor(false).state({ clusterId: q.clusterId, scopeId: q.scopeId, taskId })
    if (isTerminal(s.task_state)) break
    await sleep(CS_POLL_MS, signal)
  }
  throwIfAborted(signal)
  return (await liveConnectedApi.result({ clusterId: q.clusterId, taskId })).subgraphs
}

/** Demo: the worker port over EVERY fixture row in the window — the job ignores the queue tab. */
export async function demoConnectedSubgraphs(window: SignalWindow): Promise<CsSubgraph[]> {
  const rows = await demoSignalsApi.list(window)
  const body = connectedSignals(rows.map(workerRow), CONNECTED_SIGNALS_WEIGHT_FILTER)
  return readConnectedSignalsResult(connectedSignalsResultResponse.parse(body)).subgraphs
}

/**
 * The Storylines lens (decision S37): connected-signals subgraphs for the
 * window. Keyed by window only — the queue tab and filter row never change
 * it. `enabled` is the lens; a disabled hook still returns cached data,
 * which the panel and the AI card read without ever starting a job.
 */
export function useConnectedSignals(q: SignalsScope, window: SignalWindow, opts?: { enabled?: boolean }) {
  const demo = isDemoSession()
  const query = useQuery<CsSubgraph[]>({
    queryKey: ['connected-signals', q.clusterId, q.scopeId, window, demo ? 'demo' : 'live'],
    queryFn: ({ signal }) => (demo ? demoConnectedSubgraphs(window) : fetchConnectedSignals(q, window, signal)),
    enabled: opts?.enabled ?? true,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    // A retry would invoke a whole new job; the lens has its own Retry.
    retry: 0,
  })
  const read = useMemo(() => readSubgraphs(query.data ?? []), [query.data])
  return { ...query, subgraphs: read.subgraphs, total: read.total }
}

/**
 * Demo signal tags: the tags the demo signals carry (their tactic slugs), so a
 * tag picked in a workbook is one the demo queue already shows.
 */
export function demoSignalTags(): SignalTag[] {
  const values = new Set<string>()
  for (const r of demoSignalRows(DEMO_NOW)) {
    try {
      const list: unknown = JSON.parse(String(r.tag_list ?? '[]'))
      if (Array.isArray(list)) for (const v of list) if (typeof v === 'string' && v) values.add(v)
    } catch {
      // A malformed demo row contributes nothing.
    }
  }
  return [...values].sort().map((v) => ({ id: v, value: v, type: 'signal', color: '', enabled: true }))
}

/**
 * Tags created in demo mode, per scope. In memory only: they last the session
 * and a reload drops them (no store, so no draft contract to keep).
 */
const demoCreatedTags = new Map<string, SignalTag[]>()
const demoScopeKey = (q: SignalsScope) => `${q.clusterId}/${q.scopeId}`

const signalTagsKey = (q: SignalsScope, demo: boolean) => ['signal-tags', q.clusterId, q.scopeId, demo ? 'demo' : 'live']

/** The scope's signal tags (signals/tag/list) — the signal block's tag choices. */
export function useSignalTags(q: SignalsScope, opts?: { enabled?: boolean }) {
  const demo = isDemoSession()
  return useQuery<SignalTag[]>({
    queryKey: signalTagsKey(q, demo),
    queryFn: () =>
      demo ? Promise.resolve([...demoSignalTags(), ...(demoCreatedTags.get(demoScopeKey(q)) ?? [])]) : liveSignalsApi.tagList(q),
    enabled: opts?.enabled ?? true,
    staleTime: 5 * 60_000,
    retry: 1,
  })
}

/**
 * Create a signal tag in the scope (signals/tag/create), tag_type "general".
 * `color` is a hex string or '' for none. The ack carries no tag, so the tag
 * list refetches either way. Duplicates are the backend's to refuse; demo
 * refuses an exact repeat the same way so the options never hold two.
 */
export function useCreateSignalTag(q: SignalsScope) {
  const qc = useQueryClient()
  const demo = isDemoSession()
  return useMutation<void, Error, { value: string; color: string }>({
    mutationFn: async ({ value, color }) => {
      if (!demo) return liveSignalsApi.tagCreate({ ...q, value, color })
      const created = demoCreatedTags.get(demoScopeKey(q)) ?? []
      if ([...demoSignalTags(), ...created].some((t) => t.value === value)) throw new Error('Signal tag already exists')
      demoCreatedTags.set(demoScopeKey(q), [...created, { id: value, value, type: 'general', color, enabled: true }])
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: signalTagsKey(q, demo) }),
  })
}

/** The full document for one signal (live: a second read; demo: the same row). */
export function useSignalDocument(q: SignalsScope, uuid: string | null) {
  const demo = isDemoSession()
  return useQuery<SignalRow | null>({
    queryKey: ['signal', q.clusterId, q.scopeId, uuid, demo ? 'demo' : 'live'],
    queryFn: () => (uuid ? (demo ? demoSignalsApi.data(uuid) : liveSignalsApi.data({ ...q, uuid })) : Promise.resolve(null)),
    enabled: !!uuid,
    staleTime: 300_000,
    retry: 1,
  })
}

/** A case graph reads at most this many attached signals (docs/cases.md §Details; CB-85). */
export const SIGNAL_ROWS_CAP = 100

/**
 * Signals by id, one read each — the signals service has no read of many by
 * id (CB-85). The first SIGNAL_ROWS_CAP ids; each result is the row, null
 * when the service has none, or an error. Shares useSignalDocument's cache.
 */
export function useSignalRows(q: SignalsScope, uuids: readonly string[], enabled = true) {
  const demo = isDemoSession()
  const ids = useMemo(() => uuids.slice(0, SIGNAL_ROWS_CAP), [uuids])
  const results = useQueries({
    queries: ids.map((uuid) => ({
      queryKey: ['signal', q.clusterId, q.scopeId, uuid, demo ? 'demo' : 'live'],
      queryFn: () => (demo ? demoSignalsApi.data(uuid) : liveSignalsApi.data({ ...q, uuid })),
      enabled,
      staleTime: 300_000,
      retry: 1,
    })),
  })
  return { ids, results, total: uuids.length }
}

/** The browser's zone, sent with the facets task (the legacy drawer did the same). */
function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** The detection's knowledge base (signals/get_kb, markdown); demo states only what the signal carries. */
export function useSignalKb(q: SignalsScope, signal: Signal | null, enabled = true) {
  const demo = isDemoSession()
  return useQuery<string>({
    queryKey: ['signal-kb', q.clusterId, q.scopeId, signal?.uuid ?? null, demo ? 'demo' : 'live'],
    queryFn: async () => {
      if (!signal) return ''
      if (demo) return signal.kb || demoSignalKb(signal)
      return (await liveSignalsApi.kb({ ...q, uuid: signal.uuid })) || signal.kb
    },
    enabled: enabled && signal !== null,
    staleTime: 10 * 60_000,
    retry: 1,
  })
}

/**
 * The signal's raw-event task (lib/signal-evidence.ts): the Overview tab's
 * field breakdown, and the task whose pages are the Raw events tab. Leaving
 * the pane (the query loses its last observer) aborts the poll and revokes a
 * task that is still running.
 */
export function useSignalEvidence(q: SignalsScope, signal: Signal | null, enabled = true) {
  const demo = isDemoSession()
  const qc = useQueryClient()
  const uuid = signal?.uuid ?? null
  // A disabled query keeps its observer, so React Query would let the poll run on: when the
  // reading sections collapse or the pane is promoted to the sheet, cancel it (abort → revoke).
  useEffect(() => {
    if (!enabled && uuid !== null) void qc.cancelQueries({ queryKey: ['signal-evidence', q.clusterId, q.scopeId, uuid, demo ? 'demo' : 'live'] })
  }, [enabled, uuid, qc, q.clusterId, q.scopeId, demo])
  return useQuery<SignalEvidence>({
    queryKey: ['signal-evidence', q.clusterId, q.scopeId, uuid, demo ? 'demo' : 'live'],
    queryFn: ({ signal: abort }) => {
      if (!signal) return Promise.resolve({ taskId: null, noQuery: true, facets: [] })
      const search = searchApiFor(demo)
      const scope = { clusterId: q.clusterId, scopeId: q.scopeId }
      return fetchEvidence(
        demo
          ? {
              start: async () => (signal.rawEventQuery ? search.invoke({ ...scope, query: signal.rawEventQuery }) : null),
              state: (taskId) => search.state({ ...scope, taskId }),
              rows: async (taskId) => (await search.result({ ...scope, taskId, pageNo: 1, pageSize: 500 })).rows,
              revoke: (taskId) => search.revoke({ ...scope, taskId }),
            }
          : {
              start: () => liveSignalsApi.facetsStart({ ...scope, uuid: signal.uuid, timezone: browserZone() }),
              state: (taskId) => search.state({ ...scope, taskId }),
              facets: (taskId) => liveSignalsApi.facetsResult({ ...scope, taskId }),
              revoke: (taskId) => liveSignalsApi.facetsRevoke({ ...scope, taskId }),
            },
        abort,
      )
    },
    enabled: enabled && signal !== null,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: 0,
  })
}

/** One page of the evidence task's raw events (dispatcher task/result). */
export function useSignalEvents(q: SignalsScope, taskId: string | null, pageNo: number, pageSize = 20) {
  const demo = isDemoSession()
  return useQuery<SearchResultPage>({
    queryKey: ['signal-events', q.clusterId, q.scopeId, taskId, pageNo, pageSize, demo ? 'demo' : 'live'],
    queryFn: () => searchApiFor(demo).result({ clusterId: q.clusterId, scopeId: q.scopeId, taskId: taskId ?? '', pageNo, pageSize }),
    enabled: taskId !== null,
    staleTime: 5 * 60_000,
    // Paging keeps the last page on screen; a new task (another signal) starts empty, never on the previous signal's rows.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[3] === taskId ? prev : undefined),
    retry: 1,
  })
}

/**
 * After a signal write: refetch the window lists, tab counts and Flat pages
 * of this scope — but not the 30-day baseline (`useSignalBaseline` =
 * `useSignals('30d')`, up to LIVE_MAX / LIVE_PAGE calls). The baseline is
 * patched in place instead (`patch`, per written uuid), so the pane, which
 * reads its open signal from it on Storylines / Flat, never shows the old
 * value. Without `patch` the baseline is left as it is (the write changed
 * none of its fields).
 */
export function refreshAfterSignalWrite(
  qc: QueryClient,
  q: SignalsScope,
  write?: { uuids: readonly string[]; patch: (row: SignalRow) => SignalRow },
) {
  const demo = isDemoSession()
  const baselineKey = listKey(q, '30d', demo)
  const isBaseline = (key: readonly unknown[]) => key.length === baselineKey.length && key.every((k, i) => k === baselineKey[i])
  void qc.invalidateQueries({ queryKey: ['signals', q.clusterId, q.scopeId], predicate: (query) => !isBaseline(query.queryKey) })
  if (write) {
    const want = new Set(write.uuids)
    // The id the row parser reads (readSignal): uuid, then its live spellings.
    const idOf = (r: SignalRow) => String(r.uuid ?? r.signal_uuid ?? r.SignalUUID ?? r.id ?? '')
    qc.setQueryData<SignalRow[]>(baselineKey, (rows) => rows?.map((r) => (want.has(idOf(r)) ? write.patch(r) : r)))
  }
}

/** signals/add_signal_remark. A remarked signal leaves the server's Unassigned split, so the lists refetch. */
export function useAddRemark(q: SignalsScope) {
  const demo = isDemoSession()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { uuid: string; remark: string }) =>
      demo ? demoSignalsApi.addRemark(input.uuid, input.remark) : liveSignalsApi.addRemark({ ...q, ...input }),
    onSuccess: (_r, input) => {
      refreshAfterSignalWrite(qc, q, { uuids: [input.uuid], patch: (r) => ({ ...r, signal_remark: input.remark }) })
      void qc.invalidateQueries({ queryKey: ['signal', q.clusterId, q.scopeId] })
    },
  })
}

/** The legacy per-signal FP flag — mirrored on FP dispositions (decision S4). */
export function useFlagFalsePositive(q: SignalsScope) {
  const demo = isDemoSession()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { uuids: string[]; fp: boolean }) =>
      demo ? demoSignalsApi.flagFalsePositive(input.uuids, input.fp) : liveSignalsApi.flagFalsePositive({ ...q, ...input }),
    onSuccess: (_r, input) => refreshAfterSignalWrite(qc, q, { uuids: input.uuids, patch: (r) => ({ ...r, falsepositive: input.fp }) }),
  })
}

/* ------------------------------------------------------------- timeslice */

/** Timeslice width per window (decision S13): hour / 6h / day. */
export function sliceMsForWindow(window: SignalWindow): number {
  return window === '1d' ? 3_600_000 : window === '7d' ? 6 * 3_600_000 : 86_400_000
}

/**
 * `/api/signals/timeslice_data` rows -> buckets. VERIFIED wire shape
 * (docs/inventory/signals-capture.md): `{ count, start_time, end_time }`
 * with bucket bounds in epoch SECONDS. Junk rows are dropped; buckets come
 * back sorted so the chart never zig-zags.
 */
export function readTimesliceBuckets(rows: SignalRow[]): TimeBucket[] {
  const out: TimeBucket[] = []
  for (const r of rows) {
    const start = Number(r.start_time)
    const count = Number(r.count)
    if (!Number.isFinite(start) || !Number.isFinite(count)) continue
    out.push({ t: start * 1000, count })
  }
  return out.sort((a, b) => a.t - b.t)
}

/**
 * Demo timeslice: the fixture rows bucketed over the WHOLE window (empty
 * buckets included) so the trend reads as a timeline, not a list of
 * non-zero hours — the same numbers the demo queue shows.
 */
export function demoTimesliceBuckets(window: SignalWindow, now = Date.now()): TimeBucket[] {
  const slice = sliceMsForWindow(window)
  const span = durationToMs(window) ?? 7 * 86_400_000
  const start = Math.floor((now - span) / slice) * slice
  const n = Math.ceil((now - start) / slice)
  const counts = new Array<number>(n).fill(0)
  for (const r of demoRows()) {
    const i = Math.floor((Number(r.cnamtime) - start) / slice)
    if (i >= 0 && i < n) counts[i]!++
  }
  return counts.map((count, i) => ({ t: start + i * slice, count }))
}

/**
 * Signal counts per bucket for the selected window — the overview's trend
 * chart. Live mode hits the VERIFIED `timeslice_data` endpoint; demo mode
 * buckets the fixture. Refetches on window change only (the window is part
 * of the query key).
 */
export function useSignalTimeslice(q: SignalsScope, window: SignalWindow) {
  const demo = isDemoSession()
  return useQuery<TimeBucket[]>({
    queryKey: ['signal-timeslice', q.clusterId, q.scopeId, window, demo ? 'demo' : 'live'],
    queryFn: async () =>
      demo ? demoTimesliceBuckets(window) : readTimesliceBuckets(await liveSignalsApi.timeslice({ ...q, duration: window })),
    staleTime: 60_000,
    retry: 1,
  })
}

/* ---------------------------------------------------------------- cases */

export interface EscalateInput {
  /** Present = add to existing; absent = create then attach. */
  caseId?: string
  /** The existing case's `case-N`, for the result (the attach call does not return it). */
  caseNumber?: string | null
  title?: string
  description?: string
  severity?: CmSeverity
  assignee?: string
  uuids: string[]
}

export interface EscalateResult {
  caseId: string
  /** `case-N`; null from a service build without numbers. */
  caseNumber: string | null
  attached: boolean
  error: string | null
}

/**
 * Escalate to a V2 case (lib/case-mgmt.ts): create (title, severity,
 * description, assignee) then attach — one call per signal, the service
 * takes one. Retry-safe: when any attach fails the created case id comes
 * back with `attached: false` and the dialog retries ONLY the attach; a
 * signal already in the case (409) counts as attached.
 */
export function useEscalate(q: SignalsScope) {
  const qc = useQueryClient()
  return useMutation<EscalateResult, Error, EscalateInput>({
    mutationFn: async (input) => {
      let caseId = input.caseId ?? null
      let caseNumber = input.caseNumber ?? null
      if (!caseId) {
        const created = await createCmCase(q, {
          title: input.title?.trim() || 'Untitled case',
          severity: input.severity ?? 'medium',
          description: input.description?.trim() || undefined,
          assignee: input.assignee?.trim() || undefined,
        })
        caseId = created.id
        caseNumber = created.caseNumber
      }
      const r = await attachSignalsTo(q, caseId, input.uuids)
      const failed = r.failed.filter((f) => !/already attached/i.test(f.error))
      if (failed.length) return { caseId, caseNumber, attached: false, error: `${failed.length} of ${input.uuids.length} not attached — ${failed[0]!.error}` }
      return { caseId, caseNumber, attached: true, error: null }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['cm', q.clusterId, q.scopeId] })
      // A case attach may move signals out of the Unassigned queue; no signal row field changes, so the baseline stays.
      refreshAfterSignalWrite(qc, q)
    },
  })
}

/* ------------------------------------------------------- cases per signal */


/* ---------------------------------------------------------- stream health */

/** Demo events-today per stream; FIREWALL is silent so the chip has something to show. */
export const DEMO_DAILY_STREAMS: Record<string, number> = {
  'WIN-AUDIT': 4_120_000,
  'EP-PROCESS': 2_870_000,
  DNS: 9_450_000,
  AUTHENTICATION: 640_000,
  FIREWALL: 0,
}

/** Events received per stream SO FAR TODAY (mgmt/core/daily-stream-details — a running count, not bytes). */
export function useStreamDailyCounts(q: SignalsScope) {
  const demo = isDemoSession()
  return useQuery<Record<string, number>>({
    queryKey: ['stream-daily', q.clusterId, q.scopeId, demo ? 'demo' : 'live'],
    queryFn: () => (demo ? Promise.resolve({ ...DEMO_DAILY_STREAMS }) : liveStreamsApi.dailyEventCount(q)),
    staleTime: 5 * 60_000,
    retry: 1,
  })
}

/* ------------------------------------------------------------- lock check */

/** The demo workbook that answers private, to demo the Open workbook refusal. */
export const DEMO_PRIVATE_WORKBOOK = 'dns-query-entropy'

/**
 * POST workbook/checklockedworkbook before Open workbook. Demo: fixture
 * detections answer unlocked, DEMO_PRIVATE_WORKBOOK answers private, any
 * other id answers the captured not-found reply.
 */
export function useCheckLockedWorkbook(q: SignalsScope) {
  const demo = isDemoSession()
  return useMutation<WorkbookLockCheck, Error, { wbid: string }>({
    mutationFn: async ({ wbid }) => {
      if (!demo) return liveWorkbooksApi.checkLocked({ ...q, wbid })
      if (wbid === DEMO_PRIVATE_WORKBOOK) return { status: 'success', message: '', isPrivate: true }
      const known = demoRows().some((r) => r.Workbook === wbid)
      return known
        ? { status: 'success', message: '', isPrivate: false }
        : { status: 'success', message: 'Workbook does not exist!', isPrivate: null }
    },
  })
}

/* ---------------------------------------------------------------- helpers */

/** Signals from the same detection on the same entity, before this one. */
export function priorOnEntity(signal: Signal, all: Signal[], entityKeyOf: (e: { field: string; value: string }) => string): number {
  const keys = new Set(signal.entities.map(entityKeyOf))
  let n = 0
  for (const s of all) {
    if (s.uuid === signal.uuid || s.at >= signal.at) continue
    if (s.detectionName !== signal.detectionName) continue
    if (s.entities.some((e) => keys.has(entityKeyOf(e)))) n++
  }
  return n
}

/* ------------------------------------------------------------ storylines */

/**
 * Campaigns behind one storyline (models/signals_attribution), fetched when
 * the storyline is selected. Live sends the job's subgraph back, signal
 * nodes first (attributionPayload — CB-69); demo scores the fictional demo
 * campaigns the way the server does. Keyed by the storyline key and its
 * member count, so a re-run that changed it refetches.
 */
export function useStorylineAttribution(q: SignalsScope, sg: Subgraph | null) {
  const demo = isDemoSession()
  return useQuery<Campaign[]>({
    queryKey: ['signal-attribution', q.clusterId, q.scopeId, demo ? 'demo' : 'live', sg?.key ?? null, sg?.uuids.length ?? 0],
    queryFn: () =>
      !sg ? Promise.resolve([]) : demo ? Promise.resolve(demoAttribution(sg)) : liveSignalsApi.attribution({ ...q, graph: attributionPayload(sg.raw) }),
    enabled: sg !== null,
    staleTime: 5 * 60_000,
    retry: 1,
  })
}

